import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { logger } from '../../utils/logger';

// Animation keyframes
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  pointer-events: none;
`;

const ToastWrapper = styled.div`
  background: ${props => props.type === 'success' ? '#4CAF50' : '#f44336'};
  color: white;
  padding: 15px 20px;
  border-radius: 5px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  font-weight: 500;
  margin-bottom: 10px;
  pointer-events: auto;
  animation: ${props => props.isClosing ? slideOut : slideIn} 0.3s ease-out;
  max-width: 400px;
  word-wrap: break-word;
`;

/**
 * Secure Toast Component
 * 
 * Uses React's built-in XSS protection - all content is automatically escaped.
 * Never uses innerHTML, ensuring no XSS vulnerabilities.
 * 
 * @param {string} message - The message to display (automatically escaped)
 * @param {string} type - 'success' or 'error'
 * @param {number} duration - Duration in milliseconds (default: 3000)
 * @param {function} onClose - Callback when toast closes
 */
const Toast = ({ message, type = 'success', duration = 3000, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsClosing(true);
      // Wait for animation to complete before calling onClose
      setTimeout(() => {
        if (onClose) onClose();
      }, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  if (!message) return null;

  return (
    <ToastWrapper type={type} isClosing={isClosing}>
      {message}
    </ToastWrapper>
  );
};

/**
 * Toast Manager Component
 * Manages multiple toasts and provides a clean API
 */
export const ToastManager = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'success', duration = 3000) => {
    const id = Date.now();
    const toast = { id, message, type, duration };
    
    setToasts(prev => [...prev, toast]);

    const removeToast = () => {
      setToasts(prev => prev.filter(t => t.id !== id));
    };

    // Auto-remove after duration
    setTimeout(removeToast, duration + 300); // +300 for animation

    return id;
  };

  // Expose showToast globally (for use outside React components)
  useEffect(() => {
    window.showToast = showToast;
    return () => {
      delete window.showToast;
    };
  }, []);

  return (
    <ToastContainer>
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
        />
      ))}
    </ToastContainer>
  );
};

/**
 * Hook for showing toasts in React components
 */
export const useToast = () => {
  const showToast = (message, type = 'success', duration = 3000) => {
    if (window.showToast) {
      return window.showToast(message, type, duration);
    }
    // Fallback to logger if ToastManager not mounted
    logger.log(`[${type.toUpperCase()}] ${message}`);
  };

  return {
    showSuccess: (message, duration) => showToast(message, 'success', duration),
    showError: (message, duration) => showToast(message, 'error', duration),
  };
};

export default Toast;

